import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment as MuiInputAdornment
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person,
  Lock,
  Email,
  Business,
  CheckCircle
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import dataConnectionService from '../services/dataConnectionService';
import { UserRole, Permission } from '../types';

interface AdminSetupForm {
  companyName: string;
  adminUsername: string;
  adminPassword: string;
  confirmPassword: string;
  adminEmail: string;
}

const AdminSetup: React.FC = () => {
  const navigate = useNavigate();
  const { login, hasRole } = useAuth();
  const [formData, setFormData] = useState<AdminSetupForm>({
    companyName: '',
    adminUsername: '',
    adminPassword: '',
    confirmPassword: '',
    adminEmail: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Rol kontrolü - sadece admin erişebilir
  if (!hasRole(['admin'])) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
        <Card sx={{ borderRadius: 3, boxShadow: 4 }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h5" color="error" gutterBottom>
              Erişim Reddedildi
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Bu sayfaya erişim yetkiniz bulunmamaktadır. Sadece admin kullanıcıları admin hesabı oluşturabilir.
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => navigate('/dashboard')}
              sx={{ mt: 2 }}
            >
              Dashboard'a Dön
            </Button>
          </CardContent>
        </Card>
      </Container>
    );
  }

  const handleInputChange = (field: keyof AdminSetupForm) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    setError('');
  };

  const validateForm = (): string[] => {
    const errors: string[] = [];

    if (!formData.companyName.trim()) {
      errors.push('Şirket adı gereklidir');
    }

    if (!formData.adminUsername.trim()) {
      errors.push('Admin kullanıcı adı gereklidir');
    } else if (formData.adminUsername.length < 3) {
      errors.push('Kullanıcı adı en az 3 karakter olmalıdır');
    }

    if (!formData.adminEmail.trim()) {
      errors.push('Admin e-posta gereklidir');
    } else if (!/\S+@\S+\.\S+/.test(formData.adminEmail)) {
      errors.push('Geçerli bir e-posta adresi giriniz');
    }

    if (!formData.adminPassword) {
      errors.push('Şifre gereklidir');
    } else if (formData.adminPassword.length < 8) {
      errors.push('Şifre en az 8 karakter olmalıdır');
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/.test(formData.adminPassword)) {
      errors.push('Şifre en az bir büyük harf, bir küçük harf, bir rakam ve bir özel karakter içermelidir');
    }

    if (formData.adminPassword !== formData.confirmPassword) {
      errors.push('Şifreler eşleşmiyor');
    }

    return errors;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    const errors = validateForm();
    if (errors.length > 0) {
      setError(errors.join(', '));
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Data connection'da admin kullanıcısı oluştur
      const adminUser = {
        id: `admin_${Date.now()}`,
        username: formData.adminUsername,
        email: formData.adminEmail,
        role: UserRole.ADMIN,
        firstName: 'Admin',
        lastName: formData.companyName,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        permissions: Object.values(Permission), // Admin tüm yetkilere sahip
        companyName: formData.companyName
      };

      // Data connection'a admin kullanıcısını kaydet
      await dataConnectionService.createUser(adminUser, formData.adminPassword);
      
      setSuccess(true);
      
      // 2 saniye sonra otomatik giriş yap
      setTimeout(async () => {
        try {
          const loginResult = await login({
            username: formData.adminUsername,
            password: formData.adminPassword
          });
          
          if (loginResult) {
            navigate('/dashboard');
          } else {
            setError('Otomatik giriş başarısız. Lütfen manuel olarak giriş yapın.');
          }
        } catch (loginError) {
          setError('Otomatik giriş başarısız. Lütfen manuel olarak giriş yapın.');
        }
      }, 2000);

    } catch (error) {
      console.error('Admin hesap oluşturma hatası:', error);
      setError('Admin hesabı oluşturulurken hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Card sx={{ borderRadius: 3, boxShadow: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Admin Hesabı Oluştur
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Data bağlantısı başarıyla kuruldu! Şimdi admin hesabınızı oluşturun.
            </Typography>
          </Box>

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Başarılı!</strong> Admin hesabınız oluşturuldu. Otomatik giriş yapılıyor...
              </Typography>
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="body2">{error}</Typography>
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label="Şirket Adı"
                value={formData.companyName}
                onChange={handleInputChange('companyName')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Business />
                    </InputAdornment>
                  ),
                }}
                required
                disabled={loading}
              />

              <TextField
                fullWidth
                label="Admin Kullanıcı Adı"
                value={formData.adminUsername}
                onChange={handleInputChange('adminUsername')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person />
                    </InputAdornment>
                  ),
                }}
                required
                disabled={loading}
                helperText="En az 3 karakter"
              />

              <TextField
                fullWidth
                label="Admin E-posta"
                type="email"
                value={formData.adminEmail}
                onChange={handleInputChange('adminEmail')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email />
                    </InputAdornment>
                  ),
                }}
                required
                disabled={loading}
              />

              <FormControl fullWidth>
                <InputLabel>Admin Şifre</InputLabel>
                <OutlinedInput
                  type={showPassword ? 'text' : 'password'}
                  value={formData.adminPassword}
                  onChange={handleInputChange('adminPassword')}
                  startAdornment={
                    <MuiInputAdornment position="start">
                      <Lock />
                    </MuiInputAdornment>
                  }
                  endAdornment={
                    <MuiInputAdornment position="end">
                      <IconButton
                        onClick={togglePasswordVisibility}
                        edge="end"
                        disabled={loading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </MuiInputAdornment>
                  }
                  label="Admin Şifre"
                  required
                  disabled={loading}
                />
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Şifre Tekrar</InputLabel>
                <OutlinedInput
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={handleInputChange('confirmPassword')}
                  startAdornment={
                    <MuiInputAdornment position="start">
                      <Lock />
                    </MuiInputAdornment>
                  }
                  endAdornment={
                    <MuiInputAdornment position="end">
                      <IconButton
                        onClick={toggleConfirmPasswordVisibility}
                        edge="end"
                        disabled={loading}
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </MuiInputAdornment>
                  }
                  label="Şifre Tekrar"
                  required
                  disabled={loading}
                />
              </FormControl>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Şifre Gereksinimleri:</strong>
                  <br />• En az 8 karakter
                  <br />• En az bir büyük harf (A-Z)
                  <br />• En az bir küçük harf (a-z)
                  <br />• En az bir rakam (0-9)
                  <br />• En az bir özel karakter (@$!%*?&)
                </Typography>
              </Alert>

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
                sx={{ 
                  mt: 2,
                  py: 1.5,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  }
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} color="inherit" />
                    <Typography>Oluşturuluyor...</Typography>
                  </Box>
                ) : (
                  'Admin Hesabı Oluştur'
                )}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AdminSetup;
